import type { Dispatch, SetStateAction } from "react";
import type { ZodSchema } from "zod";
import { z } from "zod";

import { URL_BACKEND } from "../lib/constants/urls/urlBackend";
import type { OauthRefresh } from "../lib/types/schema/oauthRefreshSchema.schema";
import { consoleErrorPackagesSdk } from "../utils/consoleErrorPackagesSdk";
import { validatePromiseSchema } from "../utils/validatePromiseSchema";
import { RequestError } from "./errors/requestError.error";
import type { RequestType } from "./types/enums/requestType.enum";
import type { RequestPropsNext } from "./types/requestPropsNext.types";
import type { GetRequestHeadersProps } from "./utils/getRequestHeaders";
import { getRequestHeaders } from "./utils/getRequestHeaders";

export type RequestProps<SchemaType extends ZodSchema = any> = RequestInit &
  RequestPropsNext & {
    headersValues?: GetRequestHeadersProps["values"];
    refresh?: (refreshProps?: {
      callback: (props: { access_token: string }) => void;
    }) => Promise<OauthRefresh>;
    requestType?: RequestType;
    isRetry?: boolean;
    schema?: SchemaType;
    raw?: boolean;
    setToken?: Dispatch<SetStateAction<string>>;
  } & (
    | {
        url: string;
        urlBackendPath?: never;
      }
    | {
        url?: never;
        urlBackendPath: string;
      }
  );

export const request = async <SchemaType extends ZodSchema = any>({
  headers = {},
  headersValues,
  method = "GET",
  refresh,
  requestType = "authenticated",
  isRetry,
  schema = z.any() as any,
  url,
  urlBackendPath,
  raw = false,
  setToken,
  ...props
}: RequestProps<SchemaType>): Promise<z.infer<SchemaType>> =>
  fetch(urlBackendPath ? `${URL_BACKEND}${urlBackendPath}` : (url ?? ""), {
    credentials: requestType === "other" ? "omit" : "include",
    method,
    ...props,
    headers: {
      ...(await getRequestHeaders({ requestType, values: headersValues })),
      ...headers,
    },
  })
    .then((response): Promise<z.TypeOf<SchemaType>> => {
      if (!response.ok) throw response;

      if (response.headers.has("x-refreshed-token")) {
        /* expected structure: stringified JSON of
           { access_token: string, expires_at: string } */
        try {
          const newToken = JSON.parse(
            response.headers.get("x-refreshed-token"),
          ).access_token;
          if (newToken) {
            setToken?.(newToken);
          }
        } catch {}
      }

      if (response.status === 204) return;

      if (raw) {
        return response.text() as Promise<z.infer<SchemaType>>;
      }

      return validatePromiseSchema({
        promise: response.json(),
        schema,
      });
    })
    .catch(async (error) => {
      if (
        error.status === 401 &&
        requestType === "authenticated" &&
        urlBackendPath !== "/oauth/refresh" &&
        refresh &&
        !isRetry
      ) {
        const { access_token } = await refresh();
        if (access_token) {
          return request({
            ...props,
            headersValues: {
              ...headersValues,
              token: access_token,
            },
            urlBackendPath,
            method,
            isRetry: true,
            raw,
          });
        }
      }

      /** Ignore "AbortError: signal is aborted without reason" */
      if (error instanceof DOMException && error.name === "AbortError") return;

      const errorJson = await error?.json?.();
      const errorMessage =
        errorJson?.error?.message ?? errorJson?.error ?? error;

      consoleErrorPackagesSdk({
        error: errorMessage,
        heading: `Failed request ${method} ${urlBackendPath ? `${URL_BACKEND}${urlBackendPath}` : (url ?? "")}`,
      });

      throw new RequestError(errorMessage, error.status, errorJson.code);
    });
